import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewEncapsulation,
  Input,
  ElementRef,
  ViewChild,
  Renderer2,
  ChangeDetectionStrategy,
  OnDestroy,
  Inject
} from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import * as fromAuthStore from '@purplefront/auth/data-access';
import { HeaderFacade } from '@purplefront/header/data-access';
import { Theme, WINDOW } from '@purplefront/shared';
import * as fromHeaderStore from '@purplefront/header/data-access';
import * as fromRouterStore from '@purplefront/app-router/data-access';
import * as fromPreferencesStore from '@purplefront/preferences/data-access';
import { Feed } from '@purplefront/catalog/data-access';
import { selectSearchString } from '@purplefront/app-router/data-access';
import { AppRouterFacadeService } from '@purplefront/app-router/data-access';
import { ThemeService } from '@purplefront/shared';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomThemeModalComponent } from '../../../../../custom-theme-modal/ui/src/lib/components/custom-theme-modal/custom-theme-modal.component';
import { InterfaceLanguageService } from '@purplefront/preferences/data-access';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './header.container.html',
  styleUrls: ['./header.container.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HeaderContainer implements OnInit, OnDestroy {
  @Input() navOpen: boolean;
  @Input() querySuggest: string;
  @Output() navToggled = new EventEmitter();
  @ViewChild('searchBar') searchBar: ElementRef;

  public pagesWithoutMenu = ['on-demand', 'add-feed'];
  public isLogged: boolean;
  public hasExpandedSearchBox = false;
  public isOpenSuggestList$ = new Subject<boolean>();
  public user$: Observable<fromAuthStore.TokenCredentials>;
  public lang: string;
  public currentUrl: string;
  public currentFeed: Feed;
  public searchTerms: string = null;
  public suggestResponse$ = new Observable<any>();
  public isSuggestLoading$: Observable<boolean>;
  public searchControl: FormControl;
  public searchTerms$ = new BehaviorSubject<string>(null);
  public mobile$: Observable<boolean>;
  public tablet$: Observable<boolean>;
  public desktop$: Observable<boolean>;
  public page$: Observable<string>;
  public logoPath: string;
  public activeTheme: Theme;

  public _ngUnsubscribe$: Subject<void> = new Subject<void>();
  public _activeRouteSubscription: Subscription;
  public _resize$: Observable<number>;
  private _window: Window;

  constructor(
    public _router: Router,
    public _route: ActivatedRoute,
    public _authStore: Store<fromAuthStore.AuthState>,
    public _headerStore: Store<fromHeaderStore.HeaderState>,
    public _routerStore: Store<fromRouterStore.AppRouterState>,
    public _catalogStore: Store<fromRouterStore.AppRouterState>,
    public _preferencesStore: Store<fromPreferencesStore.PreferencesState>,
    public _renderer: Renderer2,
    public _headerService: HeaderFacade,
    public _interfaceLanguageService: InterfaceLanguageService,
    public _appRouterFacade: AppRouterFacadeService,
    public _themeService: ThemeService,
    public _modalService: NgbModal,
    @Inject(WINDOW) window: Window
  ) {
    this._window = window;
  }

  ngOnInit() {
    this.getDataFromStore();

    this.setWindowWidth();
    this.getScreenWidth();
    this.getCurrentPage();
    this.initNav();
    this.getCurrentTheme();

    /**
     * Get active route & lang
     */

    this._activeRouteSubscription = this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          this.getActiveUrl();
        })
      )
      .subscribe();

    this.getActiveUrl();

    this._routerStore
      .pipe(select(selectSearchString))
      .subscribe((searchString) => (this.hasExpandedSearchBox = !!searchString));
  }

  /*
   * Get data from Store
   */

  getDataFromStore(): void {
    /*
     * Get current Feed
     */

    this._preferencesStore
      .select(fromPreferencesStore.currentFeed)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((feed) => (this.currentFeed = feed));

    /*
     * Get current content lang
     */

    this._routerStore
      .select(fromRouterStore.selectContentLang)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((lang) => (this.lang = lang));

    /**
     * Check if user is logged or not
     */

    this._authStore.pipe(select(fromAuthStore.isLogged)).subscribe((status) => {
      this.isLogged = status;
    });

    this.user$ = this._authStore.pipe(select(fromAuthStore.tokenCredentials));
  }

  /**
   * Get url
   */

  getActiveUrl(): void {
    this.currentUrl = this._router.url.substring(0, this._router.url.lastIndexOf('/'));
  }

  /**
   * Toggle nav state (open / close)
   */
  toggleNav(): void {
    this.navOpen = !this.navOpen;
    this.navToggled.emit(this.navOpen);
  }

  /**
   * Initialize menu state (open / close)
   */
  initNav(): void {
    const observables = [this.desktop$, this.page$];
    const joint = combineLatest(observables);

    joint.pipe(takeUntil(this._ngUnsubscribe$)).subscribe(([isDesktop, page]: [boolean, string]) => {
      this.pagesWithoutMenu.includes(page) ? (this.navOpen = false) : (this.navOpen = isDesktop);
      this.navToggled.emit(this.navOpen);
    });
  }

  /**
   * Logout
   */

  logout(): void {
    this._authStore.dispatch(fromAuthStore.logout());
  }

  goToPage(): void {
    this._appRouterFacade.goToDashboardPage('dashboard');
  }

  goToPreferencesPage(): void {
    this._appRouterFacade.gotoPreferencesPage(this.lang, {
      section: 'general'
    });
  }

  goToOrderOnDemandPage(): void {
    this._appRouterFacade.goToOrderOnDemandPage();
  }

  /**
   * send the new windowWidth to the store
   */
  setWindowWidth(): void {
    this._resize$ = fromEvent(this._window, 'resize').pipe(
      takeUntil(this._ngUnsubscribe$),
      debounceTime(200),
      map(() => this._window.innerWidth),
      distinctUntilChanged(),
      startWith(this._window.innerWidth),
      tap((width) => this._headerStore.dispatch(fromHeaderStore.setScreen({ payload: { windowWidth: width } })))
    );
    this._resize$.subscribe();
  }

  /**
   * Get screen width
   */
  public getScreenWidth(): void {
    this.mobile$ = this._headerStore.pipe(takeUntil(this._ngUnsubscribe$), select(fromHeaderStore.isMobile));
    this.tablet$ = this._headerStore.pipe(takeUntil(this._ngUnsubscribe$), select(fromHeaderStore.isTablet));
    this.desktop$ = this._headerStore.pipe(takeUntil(this._ngUnsubscribe$), select(fromHeaderStore.isDesktop));
  }

  getCurrentPage(): void {
    this.page$ = this._routerStore.pipe(takeUntil(this._ngUnsubscribe$), select(fromRouterStore.selectPage));
  }

  getCurrentTheme(): void {
    this._themeService
      .getActiveTheme()
      .pipe(
        takeUntil(this._ngUnsubscribe$),
        tap((theme: Theme) => {
          this.activeTheme = theme;
          this.logoPath = this.activeTheme.logoPath;
        })
      )
      .subscribe();
  }

  openModal(): void {
    const modalRef = this._modalService.open(CustomThemeModalComponent, {
      size: 'lg',
      centered: true
    });
    modalRef.componentInstance.activeTheme = this.activeTheme;
  }

  /**
   * Prevent memory leaks unsubscribe when destroy component
   */
  ngOnDestroy(): void {
    this._ngUnsubscribe$.next();
    this._ngUnsubscribe$.complete();
  }
}
